import { useCallback } from 'react';
import { Col } from '@amzn/stencil-react-components/layout';
import {
  ProgressTracker,
  ProgressTrackerStep,
  ProgressTrackerStepStatus,
  ProgressTrackerLayout,
} from '@amzn/stencil-react-components/progress-tracker';
import { Status } from '@amzn/stencil-react-components/status-indicator';

import { ReviewWizardStep, REVIEW_WIZARD_STEP_ORDER } from '../logic/reviewTypes';

/** Per-step translated display labels. A Map (not a keyed object) so the
 *  union-typed step lookup doesn't trip security/detect-object-injection. */
type StepLabels = ReadonlyMap<ReviewWizardStep, string>;

/** Per-step error state (true = that step blocks publish), same
 *  object-injection-safe Map. */
type StepErrorFlags = ReadonlyMap<ReviewWizardStep, boolean>;

export interface ReviewWizardStepperProps {
  /** Currently-active step. */
  readonly currentStep: ReviewWizardStep;
  /** Called when the user selects a step to jump to it. */
  readonly onStepSelect: (step: ReviewWizardStep) => void;
  /** Display label per step (translated by the caller). */
  readonly labels: StepLabels;
  /** Per-step "has validation errors" flags. A step flagged `true` shows a
   *  warning sub-line (`warningText`) beneath its label so the author sees,
   *  from the stepper alone, which steps still block publish. Omitted → no step
   *  is marked. */
  readonly stepHasErrors?: StepErrorFlags;
  /** Localized sub-line shown under a step's label when it has errors (e.g.
   *  "Validation errors"). Required only when `stepHasErrors` can be true. */
  readonly warningText?: string;
}

/** Reference-stable default so an omitted `stepHasErrors` prop doesn't rebuild
 *  per render. */
const NO_STEP_ERRORS: StepErrorFlags = new Map();

/**
 * Per-step status from its position relative to the active step.
 *
 * Only the active step is `Current`; every other step (whether before or after
 * the cursor) is `Available`. We deliberately do NOT mark visited steps
 * `Complete`: `Complete` renders a checkmark, which claims the step's fields
 * are done — but merely navigating past a step doesn't validate it. Real
 * completion ticks are deferred to the publish-validation work, where a step
 * is only `Complete` once its fields actually pass validation.
 */
function resolveStepStatus(
  stepIndex: number,
  activeIndex: number,
): ProgressTrackerStepStatus {
  if (stepIndex === activeIndex) return ProgressTrackerStepStatus.Current;
  return ProgressTrackerStepStatus.Available;
}

/**
 * The 3-step progress indicator for the review wizard (Audit Criteria →
 * Sub Questions → Question Configuration).
 *
 * Thin wrapper over Stencil's `ProgressTracker`: maps our `ReviewWizardStep`
 * enum + active index onto the tracker's numeric `selectedStep` /
 * `onStepSelected`, and renders one `ProgressTrackerStep` per step with the
 * caller-supplied (translated) label. Presentational — the caller owns
 * `currentStep`.
 */
export function ReviewWizardStepper({
  currentStep,
  onStepSelect,
  labels,
  stepHasErrors = NO_STEP_ERRORS,
  warningText,
}: ReviewWizardStepperProps): JSX.Element {
  const activeIndex = REVIEW_WIZARD_STEP_ORDER.indexOf(currentStep);

  // ProgressTracker reports selection as the step's numeric index; translate
  // back to the corresponding ReviewWizardStep for the caller.
  const handleStepSelected = useCallback(
    (stepIndex: number) => {
      // `.at()` (method call, not computed index) keeps this off the
      // security/detect-object-injection sink; stepIndex is a number from
      // ProgressTracker, never user input.
      const step = REVIEW_WIZARD_STEP_ORDER.at(stepIndex);
      if (step) onStepSelect(step);
    },
    [onStepSelect],
  );

  // Full-width wrapper so the ProgressTracker's `nav > ol` stretches to fill
  // the container and its `flex: 1` steps distribute evenly across the row.
  // Without this the tracker shrinks to its content width and bunches the
  // steps into the left third.
  return (
    <Col width="100%">
      <ProgressTracker
        selectedStep={activeIndex}
        onStepSelected={handleStepSelected}
        layout={ProgressTrackerLayout.Default}
        aria-label="Review wizard steps"
      >
        {REVIEW_WIZARD_STEP_ORDER.map((step, index) => (
          <ProgressTrackerStep
            key={step}
            labelText={labels.get(step) ?? ''}
            status={resolveStepStatus(index, activeIndex)}
            // A step with validation errors shows Stencil's native status
            // indicator beneath its label: `descriptionText` supplies the
            // sub-line copy and `indicatorProps.status: Status.Negative` turns
            // the indicator's icon into the red error state the component
            // supports out of the box (the icon only renders when
            // descriptionText is set). Both are applied only when the step is
            // flagged and the caller supplied the copy.
            {...(stepHasErrors.get(step) && warningText !== undefined
              ? {
                  descriptionText: warningText,
                  indicatorProps: {
                    status: Status.Negative,
                    iconAltText: warningText,
                  },
                }
              : {})}
          />
        ))}
      </ProgressTracker>
    </Col>
  );
}
